import React, { useState, useMemo } from 'react';
import { CURATED_ICON_CATEGORIES } from '../../constants/curatedIcons';
import { ICON_COMPONENT_MAP } from './lucideIconMap';
import { X } from 'lucide-react';
import './LucideIconPicker.css';

/**
 * Icon picker for schemas.
 *
 * @param {string|null} icon - currently selected icon name (kebab-case)
 * @param {function} onChangeIcon - callback with icon name or null
 */
export default function LucideIconPicker({ icon, onChangeIcon }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(null);

  const filtered = useMemo(() => {
    let icons = category
      ? CURATED_ICON_CATEGORIES.find(c => c.label === category)?.icons || []
      : CURATED_ICON_CATEGORIES.flatMap(c => c.icons);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      icons = icons.filter(name => name.includes(q));
    }
    return [...new Set(icons)];
  }, [search, category]);

  const SelectedIcon = icon ? ICON_COMPONENT_MAP[icon] : null;

  return (
    <div className="icon-picker">
      <span className="icon-picker__label">Icon</span>

      <div className="icon-picker__trigger" onClick={() => setOpen(o => !o)}>
        <div className="icon-picker__trigger-icon">
          {SelectedIcon ? <SelectedIcon size={16} strokeWidth={2} /> : null}
        </div>
        <span className={`icon-picker__trigger-text${icon ? ' icon-picker__trigger-text--selected' : ''}`}>
          {icon ? icon : 'Choose icon...'}
        </span>
        {icon && (
          <button
            className="btn icon-picker__clear"
            onClick={e => { e.stopPropagation(); onChangeIcon(null); }}
            title="Clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="icon-picker__panel">
          <input
            className="icon-picker__search"
            type="text"
            placeholder="Search icons..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />

          <div className="icon-picker__cats">
            <button
              className={`btn icon-picker__cat${category === null ? ' icon-picker__cat--active' : ''}`}
              onClick={() => setCategory(null)}
            >All</button>
            {CURATED_ICON_CATEGORIES.map(c => (
              <button
                key={c.label}
                className={`btn icon-picker__cat${category === c.label ? ' icon-picker__cat--active' : ''}`}
                onClick={() => setCategory(c.label)}
              >{c.label}</button>
            ))}
          </div>

          <div className="icon-picker__grid">
            {filtered.length === 0 && (
              <div className="icon-picker__empty">No icons found</div>
            )}
            {filtered.map(name => {
              const Icon = ICON_COMPONENT_MAP[name];
              if (!Icon) return null;
              return (
                <button
                  key={name}
                  className={`btn icon-picker__cell${name === icon ? ' icon-picker__cell--selected' : ''}`}
                  title={name}
                  onClick={() => { onChangeIcon(name); setOpen(false); }}
                >
                  <Icon size={18} strokeWidth={2} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
